"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { toast } from "sonner";
import {
  Heart,
  Sparkles,
  MessageCircle,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  Star,
  Copy,
  Check,
} from "lucide-react";
import { trackEvent } from "@/lib/track";
import { PageLayout } from "@/components/PageLayout";
import { faqData } from "./faq-data";
import { loveMessageGroups, totalLoveMessageCount } from "./messages-data";
import { PageHero, HeroBadge } from "@/components/PageHero";
import { ModernCard, ModernCardIcon } from "@/components/ui/modern-card";
import { getBlobUrl } from "@/lib/blob-urls";
import {
  staggerContainerNormal,
  staggerItem,
  fadeInUp,
} from "@/lib/animations";
import { cn } from "@/lib/utils";

/**
 * 这个组件是从 FunnyFortunePageContent.tsx 派生的,两边的列表/复制/目录机制
 * 目前是重复的一份。刻意没有在这次改动里抽公共组件 —— funny 页是站内最值钱的
 * 页面且刚上线,不该在新建一个页面的同时去动它。抽取作为独立任务跟进。
 *
 * 图片走 unoptimized。
 *
 * next.config.js 没有开 dangerouslyAllowSVG(那是放宽安全策略,不该为两张自家
 * 插图去开),所以 /_next/image 优化器会拒绝 SVG。而 SVG 本来就没有栅格化压缩
 * 的余地,直接发原文件就是最优解。仍然用 next/image 而不是裸 <img>,是为了拿
 * width/height 带来的占位,避免 CLS —— 本站的 CLS 目标是 < 0.1。
 *
 * src 走 getBlobUrl() 而不是 getImageUrl()。两者看着差不多,差别在回退分支:
 * getImageUrl 回退到 getFullUrl(),也就是拼上站点绝对域名 —— 那是给 OG /
 * metadata 用的,页内 <img> 用它会让本地开发和 Vercel 预览部署都去请求生产域名,
 * 而这两张图在生产上要等这次部署完才存在,期间一律 404。getBlobUrl 未命中时
 * 原样返回 /images/... 相对路径,哪个环境都对。
 *
 * 现在 blob-urls.ts 里还没有这两张图;等以后跑过 scripts/upload-to-blob.js,
 * 同一行代码自动切到 CDN,不用回来改。
 */
const heroIllustration = {
  src: getBlobUrl("/images/love-fortune-cookie-messages.svg"),
  width: 800,
  height: 450,
  alt: "An opened fortune cookie with the love fortune cookie message: keep dating the person you already have",
};

const weddingIllustration = {
  src: getBlobUrl("/images/love-fortune-cookie-sayings-wedding.svg"),
  width: 800,
  height: 400,
  alt: "Three wedding fortune cookie favours with romantic fortune cookie sayings on paper slips",
};

export function LoveFortunePageContent() {
  // 记住刚复制的那一条,用来把图标换成对勾。存文案本身而不是索引,因为跨分类
  // 的索引会重复。
  const [copied, setCopied] = useState<string | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 组件卸载时清掉未触发的定时器,避免在已卸载的组件上 setState。
  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const copyMessage = useCallback((message: string) => {
    // 有意用直引号:这段文本会被粘到短信、备忘录、终端等任意目标,弯引号在那些
    // 地方可能变成乱码。与首页 handleCopy 的处理保持一致。
    const text = `"${message}"`;
    trackEvent("share_clicked", { method: "copy", surface: "love_messages" });

    // 先取出再判空,不要写成 navigator.clipboard?.writeText(text).then(...) ——
    // clipboard 缺席时可选链会短路成 undefined,紧接着的 .then() 直接抛
    // TypeError,用户按了按钮却得不到任何反馈。非安全上下文和老浏览器都会走到
    // 这条路径,所以必须显式降级到「自己选中复制」的提示。
    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      toast.error("Could not copy — try selecting the text instead.");
      return;
    }

    clipboard.writeText(text).then(
      () => {
        setCopied(message);
        toast.success("Copied");
        trackEvent("share_completed", {
          method: "copy",
          result: "copied",
          surface: "love_messages",
        });
        if (resetTimer.current) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(null), 2000);
      },
      () => toast.error("Could not copy — try selecting the text instead."),
    );
  }, []);

  return (
    <PageLayout gradient="amber">
      <PageHero
        accent="amber"
        badge={
          <HeroBadge icon={Heart} accent="amber">
            {totalLoveMessageCount} Love Fortunes
          </HeroBadge>
        }
        // H1 同时挂 Sayings / Quotes / Messages 三种词形 —— 本簇里这三种都有
        // 独立搜索量(各 260 / 260 / 110),而且是全新页面,没有需要保护的既有排名。
        // 刻意不出现 "funny":搞笑向的爱情签归 /funny-fortune-cookie-messages,
        // 两页争同一批词是这个站踩过的坑。
        title={`Love Fortune Cookie Messages: ${totalLoveMessageCount} Romantic Sayings and Quotes`}
        gradientTitle
        description={`${totalLoveMessageCount} romantic fortune cookie sayings, love quotes, and fortune cookie messages about relationships — in ${loveMessageGroups.length} themes. Tap any one to copy it.`}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Love Messages" },
        ]}
      />

      {/* 首图 + 导语。改版前这里是直接跳进列表的 —— 抓到的竞品
          (virtualedge 约 300 词、everydaypower 三个历史 H2)都有一段引导性
          散文,而本页此前一句都没有。 */}
      <section className="pt-4">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainerNormal}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-3xl"
          >
            <motion.div variants={fadeInUp}>
              <Image
                src={heroIllustration.src}
                width={heroIllustration.width}
                height={heroIllustration.height}
                alt={heroIllustration.alt}
                unoptimized
                priority
                className="mx-auto h-auto w-full max-w-2xl"
              />
            </motion.div>
            <motion.p
              variants={fadeInUp}
              className="mt-8 text-lg leading-relaxed text-slate-700 dark:text-slate-300"
            >
              A takeaway fortune cookie promises you wealth, travel, and a tall
              dark stranger. A love fortune cookie tells you to say the thing
              before you have finished rehearsing it. Below are{" "}
              {totalLoveMessageCount} love fortune cookie messages, romantic
              sayings, and quotes about relationships — written to be read out
              loud across a table, not skimmed.
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-lg leading-relaxed text-slate-700 dark:text-slate-300"
            >
              They are sorted into {loveMessageGroups.length} themes: new
              romance, long-term couples, relationships, Valentine&apos;s Day,
              wedding favours, sweet messages to send, falling in love,
              anniversaries, a crush or a first date, and the ones that ask the
              question outright. Every fortune is a button: tap it and it goes
              straight to your clipboard, ready for a homemade cookie, a place
              card, or a text you have been drafting all week.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 页内目录。150 条内容不给跳转入口,用户只能滚 —— 顺带这些锚文本本身
          就是分组标题,对页面主题的表达有帮助。 */}
      <section className="pt-12">
        <div className="container mx-auto px-4">
          <nav aria-label="Jump to a category" className="mx-auto max-w-4xl">
            <h2 className="mb-4 text-center text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              Jump to a category
            </h2>
            <ul className="flex flex-wrap justify-center gap-2">
              {loveMessageGroups.map((group) => (
                <li key={group.id}>
                  <a
                    href={`#${group.id}`}
                    className={cn(
                      "inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium",
                      "border-amber-200 bg-amber-50/70 text-amber-900",
                      "dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200",
                      "transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/40",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
                    )}
                  >
                    {group.navLabel}
                    <span className="ml-2 text-xs text-amber-700/70 dark:text-amber-300/70">
                      {group.messages.length}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          {/* 入场动画挂在**每一组**上,不是整个列表上。
              改版前这里是一个 whileInView 容器裹住全部内容 —— 那时只有 4 组
              16 条,没什么代价。现在是 10 组 150 条,容器高约 9,500px,它的顶边
              一进视口就把 160 个 motion 元素同时点着,而用户还停在页首。本站的
              INP 目标是 < 200ms,这个代价不该付。
              按组触发之后,任何时刻最多只有一组(16 个元素)在动。 */}
          <div className="space-y-12">
            {loveMessageGroups.map((group, groupIndex) => (
              <motion.div
                key={group.id}
                variants={staggerContainerNormal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
              >
                <ModernCard variant="glass" className="overflow-hidden">
                  {/* scroll-mt 必须挂在**带 id 的那个元素**上,不是外层卡片上 ——
                      浏览器按锚点目标自身的 scroll-margin 计算落点。挂错地方时
                      实测跳转会把标题顶到 viewport 顶端(top: 0),正好躲进浮动
                      导航条底下。 */}
                  <div id={group.id} className="scroll-mt-24 p-6 md:p-8">
                    {/* Category Header */}
                    <motion.div
                      variants={staggerItem}
                      className="mb-4 flex items-center gap-4"
                    >
                      <ModernCardIcon
                        gradientFrom={
                          groupIndex % 2 === 0
                            ? "from-amber-400"
                            : "from-orange-400"
                        }
                        gradientTo={
                          groupIndex % 2 === 0
                            ? "to-orange-500"
                            : "to-amber-600"
                        }
                      >
                        <Heart className="h-6 w-6 text-white" />
                      </ModernCardIcon>
                      <h2 className="font-display text-2xl font-bold text-slate-800 dark:text-white md:text-3xl">
                        {group.heading}
                      </h2>
                    </motion.div>

                    <motion.p
                      variants={staggerItem}
                      className="mb-6 max-w-3xl text-slate-600 dark:text-slate-300"
                    >
                      {group.blurb}
                    </motion.p>

                    {/* Messages Grid
                        每条签文是普通 div,不是 motion.div —— 一组 15 条 × 10 组
                        就是 150 个 motion 组件,它们各自的入场动画在视觉上完全被
                        整组的淡入盖住,除了主线程开销什么也没换来。整组一起淡入
                        就够了。 */}
                    <motion.div
                      variants={staggerItem}
                      className="grid gap-4 md:grid-cols-2"
                    >
                      {group.messages.map((message) => (
                        <div key={message}>
                          {/* 整块就是复制按钮。此前这里是个 div,却挂着
                              hover:shadow-lg + scale —— 做出可交互的样子,按下去
                              什么都不发生,比没有 hover 更糟。页面 description
                              也一直承诺「copy」却没有任何复制入口。 */}
                          <button
                            type="button"
                            onClick={() => copyMessage(message)}
                            aria-label={`Copy fortune: ${message}`}
                            className={cn(
                              "group w-full rounded-xl border p-4 text-left",
                              "bg-gradient-to-br from-white/50 to-white/30 dark:from-slate-800/50 dark:to-slate-800/30",
                              "border-white/50 dark:border-slate-700/50",
                              "transition-all duration-300 hover:scale-[1.02] hover:shadow-lg",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <Star className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                              <p className="flex-1 font-medium italic text-slate-700 dark:text-slate-200">
                                &ldquo;{message}&rdquo;
                              </p>
                              {copied === message ? (
                                <Check
                                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400"
                                  aria-hidden="true"
                                />
                              ) : (
                                <Copy
                                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          </button>
                        </div>
                      ))}
                    </motion.div>
                  </div>
                </ModernCard>

                {/* 第二张插图插在婚礼那组(索引 4)之后 —— 它画的就是婚礼小礼,
                    图文对得上,而不是随便找个位置塞图。 */}
                {groupIndex === 4 && (
                  <div className="pt-12">
                    <Image
                      src={weddingIllustration.src}
                      width={weddingIllustration.width}
                      height={weddingIllustration.height}
                      alt={weddingIllustration.alt}
                      unoptimized
                      loading="lazy"
                      className="mx-auto h-auto w-full max-w-2xl"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="bg-gradient-to-b from-transparent via-amber-50/40 to-transparent py-16 dark:via-amber-950/10 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainerNormal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="mb-4 font-display text-3xl font-bold text-slate-800 dark:text-white md:text-4xl"
            >
              How to Use These{" "}
              <span className="text-gradient-primary">Love Fortunes</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300"
            >
              Where romantic fortune cookie sayings actually land best
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainerNormal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-6 md:grid-cols-3"
          >
            {[
              {
                title: "Wedding Favours",
                // 出链放在这里而不是页尾:要「每个座位不一样」的人,下一步就是
                // 批量生成,这正是 /generator/event 的用途(免费一次 10 条,见
                // 该页文案)。本页此前只链 / 和 /generator,两个兄弟页一个都
                // 不链,而本页上线 5 周多仍是「已发现 - 尚未编入索引」。
                description: (
                  <>
                    One line per place setting, different at every seat. The
                    Weddings set is written to be read aloud between courses
                    without anybody groaning. More guests than lines?{" "}
                    <Link
                      href="/generator/event"
                      className="font-medium text-amber-700 underline decoration-amber-300 underline-offset-4 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200"
                    >
                      Party &amp; Event mode
                    </Link>{" "}
                    writes ten at a time from your names and date.
                  </>
                ),
                icon: Sparkles,
              },
              {
                title: "Just Because",
                description:
                  "Send one with no explanation. The Sweet and Short set is built for exactly that — under twelve words, no context required.",
                icon: MessageCircle,
              },
              {
                title: "Ask the Question",
                description:
                  "Bake one into a cookie and hand it over. The Will You Be Mine set does the asking when you cannot get the words out.",
                icon: Lightbulb,
              },
            ].map((tip) => (
              <motion.div key={tip.title} variants={staggerItem}>
                <ModernCard variant="feature" className="h-full">
                  <div className="p-6 text-center">
                    {/* 显式给色:ModernCardIcon 不传就走 indigo→purple 默认值,
                        整页其余部分已是 amber,漏掉这里会剩三个紫图标。 */}
                    <ModernCardIcon
                      gradientFrom="from-amber-400"
                      gradientTo="to-orange-500"
                      className="mx-auto mb-4"
                    >
                      <tip.icon className="h-6 w-6 text-white" />
                    </ModernCardIcon>
                    <h3 className="mb-2 font-display text-xl font-bold text-slate-800 dark:text-white">
                      {tip.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300">
                      {tip.description}
                    </p>
                  </div>
                </ModernCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainerNormal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <motion.div
              variants={fadeInUp}
              className="mb-4 flex items-center justify-center gap-2"
            >
              <HelpCircle className="h-6 w-6 text-amber-500" />
              <span className="font-semibold text-amber-700 dark:text-amber-300">
                FAQ
              </span>
            </motion.div>
            <motion.h2
              variants={fadeInUp}
              className="mb-4 font-display text-3xl font-bold text-slate-800 dark:text-white md:text-4xl"
            >
              Frequently Asked{" "}
              <span className="text-gradient-primary">Questions</span>
            </motion.h2>
          </motion.div>

          <motion.div
            variants={staggerContainerNormal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mx-auto max-w-3xl space-y-4"
          >
            {faqData.map((faq) => (
              <motion.div key={faq.question} variants={staggerItem}>
                <ModernCard variant="glass" className="overflow-hidden">
                  <div className="p-6">
                    <h3 className="mb-2 font-display text-lg font-bold text-slate-800 dark:text-white">
                      {faq.question}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300">
                      {faq.answer}
                    </p>
                  </div>
                </ModernCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <ModernCard
              variant="gradient"
              gradientFrom="from-amber-500"
              gradientTo="to-orange-500"
              className="overflow-hidden"
            >
              <div className="p-8 text-center md:p-12">
                <Sparkles className="mx-auto mb-4 h-12 w-12 text-white/80" />
                <h2 className="mb-4 font-display text-2xl font-bold text-white md:text-3xl">
                  Want One Nobody Has Read?
                </h2>
                <p className="mx-auto mb-6 max-w-xl text-white/80">
                  Read all {totalLoveMessageCount} already? Have the AI write a
                  romantic fortune to your own brief — a name, an occasion, a
                  tone.
                </p>
                <Link
                  href="/generator"
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-6 py-3",
                    "bg-white font-semibold text-amber-700",
                    "transition-all duration-300 hover:scale-105 hover:bg-white/90",
                    "shadow-lg hover:shadow-xl",
                  )}
                >
                  Write One with AI
                  <ChevronRight className="h-5 w-5" />
                </Link>
                {/* funny 页在同一位置用 "romantic ... sayings and quotes" 链过来,
                    这里反向链回去,锚文本写 funny —— 两页分工在链接上说清楚。
                    "funny" 只出现在这条出链里,本页 title 和各组标题仍然不带它
                    (守卫见 love-messages-content.test.ts)。 */}
                <p className="mx-auto mt-6 max-w-xl text-sm text-white/80">
                  Want something lighter? Browse{" "}
                  <Link
                    href="/funny-fortune-cookie-messages"
                    className="font-medium text-white underline decoration-white/50 underline-offset-4 hover:decoration-white"
                  >
                    funny fortune cookie messages
                  </Link>
                  .
                </p>
              </div>
            </ModernCard>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
}
